import React, { useState, useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
} from 'chart.js';
import { Line, Bar, Scatter } from 'react-chartjs-2';
import 'chart.js/auto';
import { parsePlotInstructions, buildDefaultPlot, validatePlotInstructions } from '../../utils/plotInstructions';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

const FlexiblePlot = ({ objectData, metadata, multipleObjects = null }) => {
  const [chartType, setChartType] = useState('scatter');
  const [xAxis, setXAxis] = useState('');
  const [yAxes, setYAxes] = useState([]);  // multiple y-axes
  const [availableColumns, setAvailableColumns] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [chartOptions, setChartOptions] = useState({});
  const [isExpanded, setIsExpanded] = useState(false); // Start collapsed
  const [customExpression, setCustomExpression] = useState('');
  const [customExpressions, setCustomExpressions] = useState([]);
  const chartRef = useRef(null);

  // Fixed values mode for X-axis
  const [xAxisMode, setXAxisMode] = useState('column'); // 'column' or 'fixed'
  const [fixedXValues, setFixedXValues] = useState('');
  const [invertYAxis, setInvertYAxis] = useState(false);

  // Default plot states
  const [useDefaultPlot, setUseDefaultPlot] = useState(true);
  const [defaultPlotConfigs, setDefaultPlotConfigs] = useState([]); // Array of plot configs
  const [selectedPlotIndex, setSelectedPlotIndex] = useState(0); // Which plot to show
  const [defaultPlotAvailable, setDefaultPlotAvailable] = useState(false);

  // Track if initial setup has been done
  const initialSetupDoneRef = useRef(false);

  // NEW: per-series style (keyed by axis/expr name)
  // seriesStyles: { [key: string]: { color?: string, pointRadius?: number, lineWidth?: number } }
  const [seriesStyles, setSeriesStyles] = useState({});

  const updateSeriesStyle = (seriesKey, field, value) => {
    setSeriesStyles(prev => ({
      ...prev,
      [seriesKey]: {
        ...(prev[seriesKey] || {}),
        [field]: value
      }
    }));
  };

  // Check for and build default plot from plotting_instructions
  useEffect(() => {
    console.log('🔍 [FlexiblePlot] Checking for plot instructions...');
    console.log('  - metadata:', metadata);
    console.log('  - metadata.plotting_instructions:', metadata?.plotting_instructions);

    if (!metadata || !metadata.plotting_instructions) {
      console.log('  ❌ No plotting instructions found');
      setDefaultPlotAvailable(false);
      setDefaultPlotConfigs([]);
      return;
    }

    console.log('  ✓ Found plotting instructions:', metadata.plotting_instructions);

    // Try to parse plot instructions
    const instructions = parsePlotInstructions(metadata.plotting_instructions);
    console.log('  - Parsed instructions:', instructions);

    if (!instructions) {
      console.log('  ❌ Failed to parse plotting instructions');
      setDefaultPlotAvailable(false);
      setDefaultPlotConfigs([]);
      return;
    }

    // Validate instructions
    const validation = validatePlotInstructions(instructions);
    console.log('  - Validation result:', validation);

    if (!validation.valid) {
      console.warn('  ❌ Invalid plot instructions:', validation.errors);
      setDefaultPlotAvailable(false);
      setDefaultPlotConfigs([]);
      return;
    }

    console.log('  ✓ Validation passed');

    // Build default plots for single object (multipleObjects not supported for default plots yet)
    if (objectData && !multipleObjects) {
      console.log('  - Building default plots with objectData:', objectData);
      console.log('  - Number of plots:', instructions.plots.length);

      const configs = [];
      instructions.plots.forEach((plotDef, index) => {
        const plotInstructions = { defaultPlot: plotDef };
        const config = buildDefaultPlot(plotInstructions, objectData);
        if (config) {
          configs.push(config);
          console.log(`  ✅ Plot ${index + 1} built successfully:`, plotDef.title);
        } else {
          console.log(`  ❌ Failed to build plot ${index + 1}`);
        }
      });

      if (configs.length > 0) {
        console.log(`  ✅ Built ${configs.length} plot config(s)!`);
        setDefaultPlotConfigs(configs);
        setDefaultPlotAvailable(true);
        setSelectedPlotIndex(0); // Select first plot
      } else {
        console.log('  ❌ Failed to build any plot configs');
        setDefaultPlotAvailable(false);
        setDefaultPlotConfigs([]);
      }
    } else {
      console.log('  ❌ Multiple objects mode - default plot not supported');
      setDefaultPlotAvailable(false);
      setDefaultPlotConfigs([]);
    }
  }, [metadata, objectData, multipleObjects]);

  // Initialize available columns and set initial axes - only on first load or when metadata changes
  useEffect(() => {
    if ((!objectData && !multipleObjects) || !metadata) return;
    if (initialSetupDoneRef.current && xAxis !== '') return; // Skip if already initialized and axes are set
    
    const isArrayColumn = (value) => {
      if (typeof value === 'string') {
        return value.trim().startsWith('[') && value.trim().endsWith(']');
      }
      return Array.isArray(value);
    };

    let numericColumns = [];

    if (multipleObjects && multipleObjects.length > 0) {
      const firstObj = multipleObjects[0];
      numericColumns = Object.keys(firstObj).filter(col => {
        const val = firstObj[col];
        return (typeof val === 'number' && !isNaN(val)) || isArrayColumn(val);
      });

      numericColumns = numericColumns.filter(col => {
        const numericCount = multipleObjects.filter(obj => {
          const val = obj[col];
          return (typeof val === 'number' && !isNaN(val)) || isArrayColumn(val);
        }).length;
        return numericCount >= Math.max(2, multipleObjects.length * 0.5);
      });
    } else if (objectData) {
      numericColumns = Object.keys(objectData).filter(col => {
        const val = objectData[col];
        return (typeof val === 'number' && !isNaN(val)) || isArrayColumn(val);
      });
    }
    
    setAvailableColumns(numericColumns);
    
    if (xAxis === '' && yAxes.length === 0) {
      let importantNumericColumns = [];
      
      if (metadata.important_columns && metadata.important_columns.length) {
        if (multipleObjects && multipleObjects.length > 0) {
          importantNumericColumns = metadata.important_columns.filter(col => 
            numericColumns.includes(col)
          );
        } else if (objectData) {
          importantNumericColumns = metadata.important_columns.filter(
            col => typeof objectData[col] === 'number' && !isNaN(objectData[col])
          );
        }
      }
      
      if (importantNumericColumns.length >= 2) {
        setXAxis(importantNumericColumns[0]);
        setYAxes([importantNumericColumns[1]]);
        if (importantNumericColumns.length > 2) {
          const additionalYAxes = importantNumericColumns.slice(2, 4);
          setYAxes(prev => [...prev, ...additionalYAxes]);
        }
      } else if (importantNumericColumns.length === 1 && numericColumns.length >= 2) {
        setXAxis(importantNumericColumns[0]);
        const otherColumn = numericColumns.find(col => col !== importantNumericColumns[0]);
        setYAxes([otherColumn || '']);
      } else if (numericColumns.length >= 2) {
        setXAxis(numericColumns[0]);
        setYAxes([numericColumns[1]]);
      }
    }
    
    initialSetupDoneRef.current = true;
  }, [metadata]); // Only depend on metadata changes, not on objectData or multipleObjects

  // Update available columns when objects change, but preserve selections
  useEffect(() => {
    if ((!objectData && !multipleObjects) || !metadata) return;
    
    const isArrayColumn = (value) => {
      if (typeof value === 'string') {
        return value.trim().startsWith('[') && value.trim().endsWith(']');
      }
      return Array.isArray(value);
    };

    let numericColumns = [];

    if (multipleObjects && multipleObjects.length > 0) {
      const firstObj = multipleObjects[0];
      numericColumns = Object.keys(firstObj).filter(col => {
        const val = firstObj[col];
        return (typeof val === 'number' && !isNaN(val)) || isArrayColumn(val);
      });

      numericColumns = numericColumns.filter(col => {
        const numericCount = multipleObjects.filter(obj => {
          const val = obj[col];
          return (typeof val === 'number' && !isNaN(val)) || isArrayColumn(val);
        }).length;
        return numericCount >= Math.max(2, multipleObjects.length * 0.5);
      });
    } else if (objectData) {
      numericColumns = Object.keys(objectData).filter(col => {
        const val = objectData[col];
        return (typeof val === 'number' && !isNaN(val)) || isArrayColumn(val);
      });
    }
    
    setAvailableColumns(numericColumns);
    
    if (xAxis && !numericColumns.includes(xAxis)) {
      setXAxis(numericColumns.length > 0 ? numericColumns[0] : '');
    }
    
    const validYAxes = yAxes.filter(axis => numericColumns.includes(axis));
    if (validYAxes.length !== yAxes.length) {
      setYAxes(validYAxes);
    }
  }, [objectData, multipleObjects]);

  // Helper to parse array column from string/array
  const parseArrayColumn = (value) => {
    if (Array.isArray(value)) {
      return value.map(v => Number(v)).filter(v => !isNaN(v));
    }
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed.map(v => Number(v)).filter(v => !isNaN(v));
        }
      } catch (e) {
        const match = value.match(/^\[(.*)\]$/);
        if (match) {
          const numbers = match[1].split(',').map(s => Number(s.trim())).filter(v => !isNaN(v));
          if (numbers.length > 0) return numbers;
        }
      }
    }
    return null;
  };

  // Update chart data when axes or type changes
  useEffect(() => {
    const hasValidX = (xAxisMode === 'fixed' && fixedXValues) || (xAxisMode === 'column' && xAxis);
    if (!hasValidX || (yAxes.length === 0 && customExpressions.length === 0) ||
        (!objectData && !multipleObjects)) return;

    // Fallback color generator
    const generateColor = (index) => {
      const hues = [210, 30, 120, 270, 60, 330];
      const hue = hues[index % hues.length];
      return {
        backgroundColor: `hsla(${hue}, 70%, 60%, 0.5)`,
        borderColor: `hsl(${hue}, 80%, 45%)`,
      };
    };

    // Get X values
    let xValues = [];
    if (xAxisMode === 'fixed') {
      xValues = fixedXValues.split(',').map(v => Number(v.trim())).filter(v => !isNaN(v));
    } else if (objectData && objectData[xAxis] !== undefined) {
      const arrayData = parseArrayColumn(objectData[xAxis]);
      if (arrayData) {
        xValues = arrayData;
      } else if (typeof objectData[xAxis] === 'number') {
        xValues = [objectData[xAxis]];
      }
    }

    if (xValues.length === 0) return;

    let datasets = [];

    // Y-Axis datasets
    yAxes.forEach((yAxis, yIndex) => {
      if (!objectData || objectData[yAxis] === undefined) return;

      const yArrayData = parseArrayColumn(objectData[yAxis]);
      let yValues = [];

      if (yArrayData) {
        yValues = yArrayData;
      } else if (typeof objectData[yAxis] === 'number') {
        yValues = [objectData[yAxis]];
      }

      if (yValues.length === 0) return;

      const dataPoints = [];
      const minLength = Math.min(xValues.length, yValues.length);

      for (let i = 0; i < minLength; i++) {
        dataPoints.push({ x: xValues[i], y: yValues[i] });
      }

      if (dataPoints.length > 0) {
        const styleKey = yAxis;
        const style = seriesStyles[styleKey] || {};
        const defaultColors = generateColor(yIndex);
        const color = style.color || defaultColors.borderColor;

        const pointRadius =
          typeof style.pointRadius === 'number'
            ? style.pointRadius
            : (dataPoints.length > 20 ? 0 : 5);

        const borderWidth =
          typeof style.lineWidth === 'number'
            ? style.lineWidth
            : (dataPoints.length > 20 ? 1.5 : 2);

        datasets.push({
          label: yAxis,
          data: dataPoints,
          backgroundColor: color,
          borderColor: color,
          borderWidth,
          pointRadius,
          pointHoverRadius: pointRadius ? pointRadius + 2 : 0,
          showLine: dataPoints.length > 1,
        });
      }
    });
    
    // Custom expressions datasets
    if (multipleObjects && multipleObjects.length > 0) {
      customExpressions.forEach((expression, exprIndex) => {
        const styleKey = expression;
        const style = seriesStyles[styleKey] || {};
        const defaultColors = generateColor(exprIndex + yAxes.length);
        const baseColor = style.color || defaultColors.borderColor;
        const pointRadius =
          typeof style.pointRadius === 'number'
            ? style.pointRadius
            : 6;
        const borderWidth =
          typeof style.lineWidth === 'number'
            ? style.lineWidth
            : 1.5;

        multipleObjects.forEach((obj, objIndex) => {
          const calculatedValue = calculateExpression(expression, obj);
          if (calculatedValue !== null && obj[xAxis] !== undefined) {
            let objLabel = `Object ${objIndex + 1}`;
            for (const key of Object.keys(obj)) {
              if (key !== xAxis && obj[key] !== undefined) {
                objLabel = `${key}: ${obj[key]}`;
                break;
              }
            }
            
            datasets.push({
              label: `${objLabel} (${expression})`,
              data: [{ x: obj[xAxis], y: calculatedValue }],
              backgroundColor: baseColor,
              borderColor: baseColor,
              borderWidth,
              pointRadius,
              pointHoverRadius: pointRadius + 2,
              borderDash: [5, 5],
            });
          }
        });
      });
    } else if (objectData) {
      customExpressions.forEach((expression, index) => {
        const calculatedValue = calculateExpression(expression);
        if (calculatedValue !== null) {
          const styleKey = expression;
          const style = seriesStyles[styleKey] || {};
          const defaultColors = generateColor(index + yAxes.length);
          const color = style.color || defaultColors.borderColor;
          const pointRadius =
            typeof style.pointRadius === 'number'
              ? style.pointRadius
              : 6;
          const borderWidth =
            typeof style.lineWidth === 'number'
              ? style.lineWidth
              : 1.5;

          datasets.push({
            label: expression,
            data: [{ x: objectData[xAxis], y: calculatedValue }],
            backgroundColor: color,
            borderColor: color,
            borderWidth,
            pointRadius,
            pointHoverRadius: pointRadius + 2,
            borderDash: [5, 5],
          });
        }
      });
    }

    const data = { datasets };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          title: {
            display: true,
            text: xAxisMode === 'fixed' ? 'X' : xAxis,
          },
        },
        y: {
          title: {
            display: true,
            text: datasets.length === 1 ? datasets[0].label : 'Values',
          },
          reverse: invertYAxis,
        },
      },
      plugins: {
        tooltip: {
          callbacks: {
            label: (context) => {
              const datasetLabel = context.dataset.label || '';
              return `${datasetLabel}: ${context.raw.y}, ${xAxis}: ${context.raw.x}`;
            }
          }
        },
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: datasets.length === 1 
            ? `${datasets[0].label} vs ${xAxisMode === 'fixed' ? 'X' : xAxis}` 
            : `Multiple Values vs ${xAxisMode === 'fixed' ? 'X' : xAxis}`,
        },
      },
    };

    setChartData(data);
    setChartOptions(options);
  }, [xAxis, yAxes, customExpressions, chartType, objectData, multipleObjects, xAxisMode, fixedXValues, seriesStyles, invertYAxis]);

  const handleXAxisChange = (e) => {
    setXAxis(e.target.value);
  };

  const handleYAxisToggle = (column) => {
    if (yAxes.includes(column)) {
      setYAxes(yAxes.filter(col => col !== column));
    } else {
      if (yAxes.length < 4) {
        setYAxes([...yAxes, column]);
      }
    }
  };

  const clearYAxes = () => {
    setYAxes([]);
  };
  
  const handleCustomExpressionChange = (e) => {
    setCustomExpression(e.target.value);
  };
  
  const addCustomExpression = () => {
    if (!customExpression || customExpression.trim() === '') return;
    if (customExpressions.some(expr => expr === customExpression)) {
      return;
    }
    setCustomExpressions([...customExpressions, customExpression]);
    setCustomExpression('');
  };
  
  const removeCustomExpression = (expression) => {
    setCustomExpressions(customExpressions.filter(expr => expr !== expression));
  };
  
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };
  
  const calculateExpression = (expression, objData = null) => {
    try {
      const dataToUse = objData || objectData;
      if (!dataToUse) return null;
      
      const operators = ['+', '-', '*', '/'];
      let operator = null;
      let parts = [];
      
      for (const op of operators) {
        if (expression.includes(op)) {
          operator = op;
          parts = expression.split(op);
          break;
        }
      }
      
      if (!operator || parts.length !== 2) {
        return null;
      }
      
      const leftCol = parts[0].trim();
      const rightCol = parts[1].trim();
      
      if (!(leftCol in dataToUse) || !(rightCol in dataToUse)) {
        return null;
      }
      
      const leftVal = parseFloat(dataToUse[leftCol]);
      const rightVal = parseFloat(dataToUse[rightCol]);
      
      if (isNaN(leftVal) || isNaN(rightVal)) {
        return null;
      }
      
      switch (operator) {
        case '+': return leftVal + rightVal;
        case '-': return leftVal - rightVal;
        case '*': return leftVal * rightVal;
        case '/': return rightVal !== 0 ? leftVal / rightVal : null;
        default: return null;
      }
    } catch (error) {
      console.error('Error calculating expression:', error);
      return null;
    }
  };

  const handleChartTypeChange = (e) => {
    setChartType(e.target.value);
  };

  if ((!objectData && !multipleObjects) || !metadata || availableColumns.length < 2) {
    return (
      <div className="p-6 bg-ui-light-card dark:bg-ui-dark-card shadow-md rounded-lg border border-ui-light-border dark:border-ui-dark-border">
        <h2 className="text-xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary mb-4">Data Visualization</h2>
        <p className="text-ui-light-text-secondary dark:text-ui-dark-text-secondary text-center py-4">
          Not enough numeric data available for visualization
        </p>
      </div>
    );
  }

  const renderDefaultPlot = () => {
    if (!defaultPlotConfigs || defaultPlotConfigs.length === 0) return null;

    const defaultPlotConfig = defaultPlotConfigs[selectedPlotIndex];
    if (!defaultPlotConfig) return null;

    const { type, title, xLabel, yLabel, data, labels, options = {}, isMultiSeries, seriesData } = defaultPlotConfig;

    const COLOR_PALETTE = [
      'rgb(59, 130, 246)',
      'rgb(239, 68, 68)',
      'rgb(34, 197, 94)',
      'rgb(168, 85, 247)',
      'rgb(234, 179, 8)',
      'rgb(236, 72, 153)',
      'rgb(20, 184, 166)',
      'rgb(251, 146, 60)',
    ];

    let datasets;

    if (isMultiSeries && seriesData) {
      // Multi-series: each entry in seriesData becomes its own colored dataset
      datasets = seriesData.map((series, i) => {
        const isLarge = series.data.length > 20;
        const color = series.color || COLOR_PALETTE[i % COLOR_PALETTE.length];
        const showLine = options.connectPoints || type === 'line';
        const pointRadius = showLine ? (isLarge ? 0 : 3) : (isLarge ? 3 : 5);
        const bgColor = color.replace('rgb(', 'rgba(').replace(')', ', 0.6)');
        return {
          label: series.label,
          data: series.data,
          backgroundColor: showLine && isLarge ? 'transparent' : bgColor,
          borderColor: color,
          borderWidth: isLarge ? 1.5 : 2,
          pointRadius,
          pointHoverRadius: pointRadius + 2,
          showLine,
          fill: false,
        };
      });
    } else if (data) {
      const isArrayPlot = data.length > 20;

      if (isArrayPlot) {
        datasets = [{
          label: yLabel,
          data: data,
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderColor: 'rgb(59, 130, 246)',
          borderWidth: 1.5,
          pointRadius: 0,
          pointHoverRadius: 3,
          showLine: true,
          fill: false,
        }];
      } else {
        datasets = data.map((point, index) => {
          const hue = (index * 360) / data.length;
          return {
            label: labels && labels[index] ? labels[index] : `Point ${index + 1}`,
            data: [point],
            backgroundColor: `hsla(${hue}, 70%, 60%, 0.6)`,
            borderColor: `hsl(${hue}, 80%, 50%)`,
            borderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7,
            showLine: false,
          };
        });

        if (options.connectPoints) {
          datasets.push({
            label: 'Connection Line',
            data: data,
            backgroundColor: 'transparent',
            borderColor: 'rgba(100, 100, 100, 0.3)',
            borderWidth: 1.5,
            pointRadius: 0,
            showLine: true,
            fill: false,
            order: 1,
          });
        }
      }
    } else {
      return null;
    }

    const chartData = { datasets };

    const isArrayPlot = !isMultiSeries && data && data.length > 20;
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: isMultiSeries
            ? (options.showLegend !== false)
            : (isArrayPlot ? false : (options.showLegend !== false)),
          position: 'right',
          labels: {
            boxWidth: 12,
            font: { size: 11 }
          }
        },
        title: {
          display: !!title,
          text: title || 'Default Plot',
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              if (isMultiSeries) {
                const x = typeof context.parsed.x === 'number' ? context.parsed.x.toPrecision(6) : context.parsed.x;
                const y = typeof context.parsed.y === 'number' ? context.parsed.y.toPrecision(6) : context.parsed.y;
                return `${context.dataset.label}: (${x}, ${y})`;
              }
              const index = context.dataIndex;
              const bandLabel = labels && labels[index] ? labels[index] : '';
              return `${bandLabel}: (${context.parsed.x}, ${context.parsed.y})`;
            }
          }
        }
      },
      scales: {
        x: {
          type: 'linear',
          title: {
            display: true,
            text: xLabel || 'X'
          }
        },
        y: {
          type: 'linear',
          title: {
            display: true,
            text: yLabel || 'Y'
          },
          reverse: options.invertYAxis === true,
        }
      }
    };

    switch (type) {
      case 'line':
        return <Line data={chartData} options={chartOptions} />;
      case 'bar':
        return <Bar data={chartData} options={chartOptions} />;
      case 'scatter':
      default:
        return <Scatter data={chartData} options={chartOptions} />;
    }
  };

  const renderChart = () => {
    if (!chartData || (!xAxis && xAxisMode === 'column') || (yAxes.length === 0 && customExpressions.length === 0)) {
      return (
        <div className="flex items-center justify-center h-full">
          <p className="text-ui-light-text-secondary dark:text-ui-dark-text-secondary text-center py-4">
            {(!xAxis && xAxisMode === 'column') ? "Please select an X-axis" : 
             (yAxes.length === 0 && customExpressions.length === 0) ? "Please select at least one Y-axis or add a custom expression" : 
             "Chart data not available"}
          </p>
        </div>
      );
    }

    switch (chartType) {
      case 'line':
        return <Line ref={chartRef} data={chartData} options={chartOptions} />;
      case 'bar':
        return <Bar ref={chartRef} data={chartData} options={chartOptions} />;
      case 'scatter':
      default:
        return <Scatter ref={chartRef} data={chartData} options={chartOptions} />;
    }
  };

  // Series keys for styling panel
  const styleSeriesKeys = [...yAxes, ...customExpressions];

  return (
    <div className="bg-ui-light-card dark:bg-ui-dark-card shadow-md rounded-lg border border-ui-light-border dark:border-ui-dark-border">
      {/* Header with expand/collapse button */}
      <div
        className="p-4 flex justify-between items-center hover:bg-ui-light-background dark:hover:bg-ui-dark-background/50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">Data Visualization</h2>
          {defaultPlotAvailable && (
            <div className="flex items-center gap-2 bg-ui-light-background dark:bg-ui-dark-background px-3 py-1 rounded-md border border-ui-light-border dark:border-ui-dark-border">
              <button
                onClick={() => setUseDefaultPlot(true)}
                className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                  useDefaultPlot
                    ? 'bg-primary-500 text-white'
                    : 'text-ui-light-text-secondary dark:text-ui-dark-text-secondary hover:text-ui-light-text-primary dark:hover:text-ui-dark-text-primary'
                }`}
              >
                Default
              </button>
              <button
                onClick={() => setUseDefaultPlot(false)}
                className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                  !useDefaultPlot
                    ? 'bg-primary-500 text-white'
                    : 'text-ui-light-text-secondary dark:text-ui-dark-text-secondary hover:text-ui-light-text-primary dark:hover:text-ui-dark-text-primary'
                }`}
              >
                Custom
              </button>
            </div>
          )}
        </div>
        <button
          onClick={toggleExpanded}
          className="text-ui-light-text-secondary dark:text-ui-dark-text-secondary hover:text-ui-light-text-primary dark:hover:text-ui-dark-text-primary focus:outline-none transition-colors"
        >
          {isExpanded ? (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          ) : (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </button>
      </div>
      
      {/* Collapsible content */}
      {isExpanded && (
        <div className="p-6 border-t border-ui-light-border dark:border-ui-dark-border">
          {/* Notice when viewing controls in default mode */}
          {useDefaultPlot && defaultPlotAvailable && (
            <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-md">
              <p className="text-sm text-yellow-800 dark:text-yellow-300">
                <strong>Note:</strong> You're currently viewing the default plot. Click <strong>"Custom"</strong> above to use these controls for custom plotting.
              </p>
            </div>
          )}

          {/* === 1. Chart & Axes configuration === */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
            {/* Chart type */}
            <div>
              <label className="block text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mb-1">Chart Type</label>
              <select
                value={chartType}
                onChange={handleChartTypeChange}
                className="w-full rounded-md border border-ui-light-border dark:border-ui-dark-border bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-primary dark:text-ui-dark-text-primary shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:focus:ring-primary-400"
              >
                <option value="scatter">Scatter Plot</option>
                <option value="line">Line Chart</option>
                <option value="bar">Bar Chart</option>
              </select>
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={invertYAxis}
                  onChange={(e) => setInvertYAxis(e.target.checked)}
                  className="rounded text-primary-600 dark:text-primary-400 focus:ring-primary-500"
                />
                <span className="text-xs text-ui-light-text-secondary dark:text-ui-dark-text-secondary">Invert Y-axis</span>
              </label>
            </div>
            
            {/* X-axis config */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">X-Axis</label>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setXAxisMode('column')}
                    className={`px-2 py-0.5 text-xs rounded ${xAxisMode === 'column' ? 'bg-primary-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                  >
                    Column
                  </button>
                  <button
                    type="button"
                    onClick={() => setXAxisMode('fixed')}
                    className={`px-2 py-0.5 text-xs rounded ${xAxisMode === 'fixed' ? 'bg-primary-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                  >
                    Fixed
                  </button>
                </div>
              </div>
              {xAxisMode === 'column' ? (
                <input
                  type="text"
                  list="x-axis-columns"
                  value={xAxis}
                  onChange={handleXAxisChange}
                  placeholder="Type to search columns..."
                  className="w-full rounded-md border border-ui-light-border dark:border-ui-dark-border bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-primary dark:text-ui-dark-text-primary shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:focus:ring-primary-400 px-3 py-2"
                />
              ) : (
                <input
                  type="text"
                  value={fixedXValues}
                  onChange={(e) => setFixedXValues(e.target.value)}
                  placeholder="e.g., 3485,3785,3950,..."
                  className="w-full rounded-md border border-ui-light-border dark:border-ui-dark-border bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-primary dark:text-ui-dark-text-primary shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:focus:ring-primary-400 px-3 py-2"
                />
              )}
              <datalist id="x-axis-columns">
                {availableColumns.map((col) => (
                  <option key={`x-${col}`} value={col} />
                ))}
              </datalist>
              {xAxisMode === 'fixed' && (
                <p className="text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary mt-1">
                  Comma-separated values
                </p>
              )}
            </div>
            
            {/* Y axes selected */}
            <div className="lg:col-span-2">
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Y-Axes (select up to 4)</label>
                {yAxes.length > 0 && (
                  <button 
                    className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300" 
                    onClick={clearYAxes}
                  >
                    Clear All
                  </button>
                )}
              </div>
              
              <div className="flex flex-wrap gap-2 border border-ui-light-border dark:border-ui-dark-border rounded-md p-2 min-h-[38px] bg-ui-light-background dark:bg-ui-dark-background">
                {yAxes.length === 0 && customExpressions.length === 0 && (
                  <span className="text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary italic">No columns selected</span>
                )}
                
                {yAxes.map(col => (
                  <span 
                    key={`selected-${col}`}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300"
                  >
                    {col}
                    <button 
                      type="button"
                      className="ml-1 inline-flex text-primary-500 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 focus:outline-none"
                      onClick={() => handleYAxisToggle(col)}
                    >
                      <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </span>
                ))}
                
                {customExpressions.map(expr => (
                  <span 
                    key={`expr-${expr}`}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300"
                  >
                    {expr}
                    <button 
                      type="button"
                      className="ml-1 inline-flex text-purple-500 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 focus:outline-none"
                      onClick={() => removeCustomExpression(expr)}
                    >
                      <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
          
          {/* === 2. Add Y-axis column (autocomplete) === */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mb-2">
              Add Y-Axis Column {yAxes.length >= 4 && <span className="text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">(maximum 4 reached)</span>}
            </label>
            <input
              type="text"
              list="y-axis-columns"
              placeholder="Type to search and select..."
              disabled={yAxes.length >= 4}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target.value) {
                  const value = e.target.value;
                  if (!yAxes.includes(value) && yAxes.length < 4 && availableColumns.includes(value)) {
                    handleYAxisToggle(value);
                    e.target.value = '';
                  }
                }
              }}
              onChange={(e) => {
                const value = e.target.value;
                if (availableColumns.includes(value) && !yAxes.includes(value) && yAxes.length < 4) {
                  handleYAxisToggle(value);
                  e.target.value = '';
                }
              }}
              className="w-full rounded-md border border-ui-light-border dark:border-ui-dark-border bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-primary dark:text-ui-dark-text-primary shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:focus:ring-primary-400 disabled:opacity-50 px-3 py-2"
            />
            <datalist id="y-axis-columns">
              {availableColumns
                .filter(col => col !== xAxis && !yAxes.includes(col))
                .map(col => (
                  <option key={`avail-${col}`} value={col} />
                ))}
            </datalist>
            <p className="mt-1 text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
              {availableColumns.filter(col => col !== xAxis && !yAxes.includes(col)).length} columns available. Press Enter or select from dropdown.
            </p>
          </div>

          {/* === 3. Custom expressions (now below Y-axis adder) === */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mb-1">
              Custom Expression (e.g., "column1-column2")
            </label>
            <div className="flex">
              <input
                type="text"
                value={customExpression}
                onChange={handleCustomExpressionChange}
                placeholder="e.g., mag_g-mag_r"
                className="flex-1 rounded-l-md border border-ui-light-border dark:border-ui-dark-border bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-primary dark:text-ui-dark-text-primary shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:focus:ring-primary-400 px-3 py-2"
              />
              <button
                type="button"
                onClick={addCustomExpression}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-r-md bg-primary-600 dark:bg-primary-500 hover:bg-primary-700 dark:hover:bg-primary-400 text-white focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-colors"
              >
                Add
              </button>
            </div>
            <p className="mt-1 text-xs text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
              Supported operations: + (addition), - (subtraction), * (multiplication), / (division)
            </p>
          </div>

          {/* === 4. Series style controls (color, point size, line width) === */}
          {styleSeriesKeys.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-ui-light-text-primary dark:text-ui-dark-text-primary mb-2">
                Series Style
              </h3>
              <p className="text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary mb-3">
                Customize color, point size and line width for each series (Y-axis or expression).
              </p>
              <div className="space-y-2">
                {styleSeriesKeys.map(key => {
                  const style = seriesStyles[key] || {};
                  return (
                    <div
                      key={`style-${key}`}
                      className="flex flex-wrap items-center gap-3 border border-ui-light-border dark:border-ui-dark-border rounded-md px-3 py-2 bg-ui-light-background dark:bg-ui-dark-background"
                    >
                      <div className="text-xs font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary min-w-[120px]">
                        {key}
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                          Color
                        </label>
                        <input
                          type="color"
                          value={style.color || '#3b82f6'}
                          onChange={(e) => updateSeriesStyle(key, 'color', e.target.value)}
                          className="h-6 w-10 border border-ui-light-border dark:border-ui-dark-border rounded"
                        />
                      </div>
                      <div className="flex items-center gap-1">
                        <label className="text-xs text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                          Point size
                        </label>
                        <input
                          type="number"
                          min={0}
                          max={20}
                          value={style.pointRadius ?? ''}
                          onChange={(e) => {
                            const v = e.target.value === '' ? undefined : Number(e.target.value);
                            updateSeriesStyle(key, 'pointRadius', isNaN(v) ? undefined : v);
                          }}
                          className="w-16 rounded-md border border-ui-light-border dark:border-ui-dark-border bg-ui-light-background dark:bg-ui-dark-background text-xs px-1 py-0.5"
                        />
                      </div>
                      <div className="flex items-center gap-1">
                        <label className="text-xs text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                          Line width
                        </label>
                        <input
                          type="number"
                          min={0}
                          max={10}
                          value={style.lineWidth ?? ''}
                          onChange={(e) => {
                            const v = e.target.value === '' ? undefined : Number(e.target.value);
                            updateSeriesStyle(key, 'lineWidth', isNaN(v) ? undefined : v);
                          }}
                          className="w-16 rounded-md border border-ui-light-border dark:border-ui-dark-border bg-ui-light-background dark:bg-ui-dark-background text-xs px-1 py-0.5"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Chart is always visible */}
      <div className="p-6 border-t border-ui-light-border dark:border-ui-dark-border">
        {useDefaultPlot && defaultPlotAvailable && defaultPlotConfigs.length > 0 ? (
          <div>
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                  Showing default plot from plotting instructions
                </div>
                {defaultPlotConfigs.length > 1 && (
                  <select
                    value={selectedPlotIndex}
                    onChange={(e) => setSelectedPlotIndex(Number(e.target.value))}
                    className="px-2 py-1 text-sm bg-ui-light-background dark:bg-ui-dark-background border border-ui-light-border dark:border-ui-dark-border rounded"
                  >
                    {defaultPlotConfigs.map((config, index) => (
                      <option key={index} value={index}>
                        {config.title || `Plot ${index + 1}`}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
              >
                {isExpanded ? 'Hide Details' : 'Show Details'}
              </button>
            </div>
            {isExpanded && defaultPlotConfigs[selectedPlotIndex] && (
              <div className="mb-4 p-3 bg-ui-light-background dark:bg-ui-dark-background rounded border border-ui-light-border dark:border-ui-dark-border text-sm">
                <div className="grid grid-cols-2 gap-2 text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                  <div><span className="font-medium">Plot Type:</span> {defaultPlotConfigs[selectedPlotIndex].type}</div>
                  <div><span className="font-medium">Connect Points:</span> {defaultPlotConfigs[selectedPlotIndex].options.connectPoints ? 'Yes' : 'No'}</div>
                  <div><span className="font-medium">X-Axis:</span> {defaultPlotConfigs[selectedPlotIndex].xLabel}</div>
                  <div><span className="font-medium">Y-Axis:</span> {defaultPlotConfigs[selectedPlotIndex].yLabel}</div>
                  {defaultPlotConfigs[selectedPlotIndex].options.invertYAxis && (
                    <div className="col-span-2"><span className="font-medium">Y-Axis Inverted</span> (magnitudes increase downward)</div>
                  )}
                  <div className="col-span-2">
                    <span className="font-medium">Data Points:</span> {defaultPlotConfigs[selectedPlotIndex].data.length}
                    {defaultPlotConfigs[selectedPlotIndex].labels && defaultPlotConfigs[selectedPlotIndex].labels.length > 0 && defaultPlotConfigs[selectedPlotIndex].data.length <= 20 && (
                      <span className="ml-2">({defaultPlotConfigs[selectedPlotIndex].labels.join(', ')})</span>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="h-80 w-full">
              {renderDefaultPlot()}
            </div>
          </div>
        ) : (
          <div className="h-80 w-full">
            {renderChart()}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlexiblePlot;